import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import {
  XMarkIcon,
  CodeBracketIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import React, { Fragment } from 'react';

interface SyntaxHelperModalProps {
  open: boolean;
  onClose: () => void;
}

const SyntaxHelperModal: React.FC<SyntaxHelperModalProps> = ({
  open,
  onClose,
}) => {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto pt-16 sm:pt-20">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6 lg:p-8">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative flex flex-col transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all w-full max-w-7xl ring-1 ring-slate-200 max-h-[85vh] sm:max-h-[calc(100vh-8rem)]">
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-5 border-b border-slate-100 flex items-center justify-between shrink-0 z-10">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                      <CodeBracketIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <DialogTitle
                        as="h3"
                        className="text-xl font-bold text-slate-900 tracking-tight"
                      >
                        Rule Syntax Overview
                      </DialogTitle>
                      <p className="text-sm text-slate-500 font-medium mt-0.5">
                        Quick reference for logical formulas and functions
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rounded-md bg-white p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all border border-transparent hover:border-slate-200 shadow-sm"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close syntax guide</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>

                {/* Content Body */}
                <div className="p-6 md:p-8 bg-slate-50/50 overflow-y-auto flex-1">
                  <div className="mx-auto max-w-full space-y-8">
                    {/* Fundamentals Section */}
                    <section className="min-w-full">
                      <div className="flex items-center gap-2 mb-4 px-1">
                        <CubeIcon className="w-5 h-5 text-indigo-500" />
                        <h4 className="text-lg font-bold text-slate-800 tracking-tight">
                          Basic Fundamentals
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-slate-200/60 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all group">
                          <strong className="block text-slate-800 text-sm mb-1.5 group-hover:text-indigo-700 transition-colors">
                            Fields and Documents
                          </strong>
                          <p className="text-slate-500 text-[13px] leading-relaxed mb-3">
                            Must be enclosed within square brackets.
                          </p>
                          <div className="inline-flex rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1">
                            <code className="text-indigo-600 font-mono text-xs font-semibold">
                              [Status]
                            </code>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-200/60 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all group">
                          <strong className="block text-slate-800 text-sm mb-1.5 group-hover:text-indigo-700 transition-colors">
                            Functions
                          </strong>
                          <p className="text-slate-500 text-[13px] leading-relaxed mb-3">
                            Always include parenthesis after name.
                          </p>
                          <div className="inline-flex rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1">
                            <code className="text-pink-600 font-mono text-xs font-semibold">
                              isexist()
                            </code>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-200/60 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all group">
                          <strong className="block text-slate-800 text-sm mb-1.5 group-hover:text-indigo-700 transition-colors">
                            Equality & Math
                          </strong>
                          <p className="text-slate-500 text-[13px] leading-relaxed mb-3">
                            To compare expressions, use equality ops.
                          </p>
                          <div className="inline-flex rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1">
                            <code className="text-indigo-600 font-mono text-xs font-semibold">
                              <span className="text-slate-400">==</span>,{' '}
                              <span className="text-slate-400">!=</span>,{' '}
                              <span className="text-slate-400">&gt;</span>,{' '}
                              <span className="text-slate-400">&lt;</span>,{' '}
                              <span className="text-slate-400">&gt;=</span>,{' '}
                              <span className="text-slate-400">&lt;=</span>
                            </code>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-slate-200/60 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all group">
                          <strong className="block text-slate-800 text-sm mb-1.5 group-hover:text-indigo-700 transition-colors">
                            Strings
                          </strong>
                          <p className="text-slate-500 text-[13px] leading-relaxed mb-3">
                            Must be wrapped in single or double quotes.
                          </p>
                          <div className="inline-flex rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1">
                            <code className="text-emerald-600 font-mono text-xs font-semibold">
                              'Open'
                            </code>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Functions Section */}
                    <section className="min-w-full">
                      <div className="flex items-center gap-2 mb-4 px-1">
                        <CodeBracketIcon className="w-5 h-5 text-indigo-500" />
                        <h4 className="text-lg font-bold text-slate-800 tracking-tight">
                          Available Functions
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              if(cond, if_true, if_false)
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              Evaluates the 1st argument (condition). If
                              satisfied, 2nd arg executes. If not, 3rd arg
                              executes.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              in([field], [val1], ...)
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              The 1st argument is compared against other values.
                              Returns true if it matches{' '}
                              <span className="font-medium text-slate-800">
                                any
                              </span>{' '}
                              value.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              compare([field],[val1],...)
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              Similar to 'in', but checks if the 1st value
                              matches{' '}
                              <span className="font-medium text-slate-800">
                                all
                              </span>{' '}
                              subsequent arguments. Returns true if all match.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              nearMatch([field],[val1],...)
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              Like compare(), but treats values as a match when
                              they are the same after address-aware
                              normalization (case, punctuation, whitespace,
                              Street/St) or their similarity is at least 0.85.
                              Use for street names and addresses. Returns true
                              if all arguments near-match the first.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              empty([field])
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              Checks if the specified field contains no value
                              (null or empty string). Returns true if empty.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              isnotempty([field])
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              The inverse of empty(). Returns true if the field
                              contains any valid value or character.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              isexist([doc1],[doc2],...)
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              Validates document existence. Returns true
                              strictly when{' '}
                              <span className="font-medium text-slate-800">
                                ALL
                              </span>{' '}
                              mentioned documents are present.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              isexistany([doc1], ...)
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              Validates document existence loosely. Returns true
                              if{' '}
                              <span className="font-medium text-slate-800">
                                AT LEAST ONE
                              </span>{' '}
                              document is present.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              checklength([field] == 5)
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              Calculates length. Accepts an internal comparison
                              operator with lengths to validate string bounds.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              datedif([date1] | today() |
                              &apos;mm/dd/yyyy&apos;, [date2] | today() |
                              &apos;mm/dd/yyyy&apos;)
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              Computes elapsed time between two dates. Each
                              parameter can be a{' '}
                              <span className="font-medium text-slate-800">
                                date field reference
                              </span>
                              ,{' '}
                              <span className="font-medium text-slate-800">
                                today()
                              </span>
                              , or a{' '}
                              <span className="font-medium text-slate-800">
                                quoted date string
                              </span>{' '}
                              (e.g. &apos;01/15/2026&apos;). Returns the
                              difference as an integer.
                            </p>
                          </div>
                        </div>

                        <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all flex flex-col overflow-hidden group">
                          <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-100 group-hover:bg-indigo-50/30 transition-colors">
                            <code className="font-mono text-xs font-bold text-indigo-700 break-all">
                              checkall([field])
                            </code>
                          </div>
                          <div className="p-4 flex-1">
                            <p className="text-slate-600 text-[13px] leading-relaxed">
                              Globally checks the field against all exact match
                              fields globally across documents for consistency.
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SyntaxHelperModal;
